<template>
  <div class="experiment-container">
    <h2>Эксперимент: Построение изображения в линзе</h2>
    <div class="experiment-area">
      <svg :width="canvasWidth" :height="canvasHeight" class="canvas">
        <!-- Главная ось -->
        <line
          :x1="0"
          :y1="canvasHeight / 2"
          :x2="canvasWidth"
          :y2="canvasHeight / 2"
          stroke="black"
          stroke-width="2"
        />
        
        <!-- Линза (теперь округлая) -->
        <path
          :d="lensPath"
          fill="rgba(0, 150, 255, 0.2)"
          stroke="blue"
        />
        
        <!-- Фокусные точки -->
        <circle
          :cx="lensPosition + focalLength"
          :cy="canvasHeight / 2"
          r="5"
          fill="red"
        />
        <circle
          :cx="lensPosition - focalLength"
          :cy="canvasHeight / 2"
          r="5"
          fill="red"
        />
        
        <!-- Объект -->
        <line
          :x1="objectPosition"
          :y1="canvasHeight / 2"
          :x2="objectPosition"
          :y2="canvasHeight / 2 - objectHeight"
          stroke="green"
          stroke-width="4"
          @mousedown.prevent="startDragging('object')"
        />
        
        <!-- Изображение -->
        <line
          v-if="imageDistance > 0"
          :x1="lensPosition + imageDistance"
          :y1="canvasHeight / 2"
          :x2="lensPosition + imageDistance"
          :y2="canvasHeight / 2 - imageHeight"
          stroke="red"
          stroke-width="4"
        />
        
        <!-- Анимированные лучи -->
        <line
          v-for="(ray, index) in rays"
          :key="index"
          :x1="ray.x1"
          :y1="ray.y1"
          :x2="ray.x2"
          :y2="ray.y2"
          class="ray"
        />
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: "LensExperiment",
  data() {
    return {
      canvasWidth: 1000,
      canvasHeight: 600,
      lensPosition: 600,
      lensWidth: 30,
      lensCurvature: 100, // Кривизна линзы
      focalLength: 100,
      objectPosition: 200,
      objectHeight: 50,
      dragging: null,
    };
  },
  computed: {
    // Форма линзы (теперь округлая)
    lensPath() {
      const yMid = this.canvasHeight / 2;
      const lensTop = yMid - this.canvasHeight / 2;
      const lensBottom = yMid + this.canvasHeight / 2;
      
      // Левая и правая стороны линзы с кривизной
      const leftX = this.lensPosition - this.lensWidth/2;
      const rightX = this.lensPosition + this.lensWidth/2;
      
      return `
        M ${leftX} ${yMid - this.lensCurvature}
        Q ${leftX} ${lensTop}, ${this.lensPosition} ${lensTop}
        Q ${rightX} ${lensTop}, ${rightX} ${yMid - this.lensCurvature}
        Q ${rightX} ${lensBottom}, ${this.lensPosition} ${lensBottom}
        Q ${leftX} ${lensBottom}, ${leftX} ${yMid + this.lensCurvature}
        Q ${leftX} ${yMid}, ${leftX} ${yMid - this.lensCurvature}
        Z
      `;
    },
    
    imageDistance() {
      const D_o = this.lensPosition - this.objectPosition;
      if (D_o <= this.focalLength) return -1;
      return (this.focalLength * D_o) / (D_o - this.focalLength);
    },
    
    imageHeight() {
      return (-this.imageDistance / (this.lensPosition - this.objectPosition)) * this.objectHeight;
    },
    
    rays() {
      const yMid = this.canvasHeight / 2;
      const objectTop = yMid - this.objectHeight;
      const lensX = this.lensPosition;
      const imageX = lensX + this.imageDistance;
      const imageY = yMid - this.imageHeight;
      const focalX = lensX + this.focalLength;

      return [
        // Луч 1: Параллельный оси -> через фокус
        { x1: this.objectPosition, y1: objectTop, x2: lensX, y2: objectTop },
        { x1: lensX, y1: objectTop, x2: imageX, y2: imageY },
        
        // Луч 2: Через центр линзы (не преломляется)
        { x1: this.objectPosition, y1: objectTop, x2: lensX, y2: yMid },
        { x1: lensX, y1: yMid, x2: imageX, y2: imageY },
        
        // Луч 3: Через фокус -> параллельно оси
        { x1: this.objectPosition, y1: objectTop, x2: focalX, y2: yMid },
        { x1: focalX, y1: yMid, x2: imageX, y2: yMid },
        { x1: imageX, y1: yMid, x2: imageX, y2: imageY }
      ];
    },
  },
  methods: {
    startDragging(target) {
      this.dragging = target;
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDragging);
    },
    onDrag(event) {
      if (this.dragging === "object") {
        const canvas = document.querySelector(".canvas");
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        this.objectPosition = Math.max(0, Math.min(mouseX, this.lensPosition - 10));
      }
    },
    stopDragging() {
      this.dragging = null;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDragging);
    },
  },
};
</script>

<style scoped>
/* Контейнер */
.experiment-container {
  text-align: center;
  font-family: Arial, sans-serif;
}

.experiment-area {
  margin: 20px auto;
  border: 1px solid #ccc;
  background: #f9f9f9;
}

.canvas {
  display: block;
  margin: 0 auto;
  border: 1px solid #000;
  width: 100%;
}

/* Анимация лучей */
.ray {
  stroke: orange;
  stroke-width: 2;
  stroke-dasharray: 5, 5;
  animation: ray-animation 2s linear infinite;
}

/* Анимация движения */
@keyframes ray-animation {
  0% {
    stroke-dashoffset: 50;
  }
  100% {
    stroke-dashoffset: 0;
  }
}
</style>